import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getForm, createForm, updateForm, getDepartments, getGlobalDropdowns, uploadFile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Trash2, GripVertical, Settings, ChevronDown, ChevronUp, Upload, X, Image, Copy, Eye, EyeOff, History, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'textarea', label: 'Textbereich', icon: '📄' },
  { value: 'email', label: 'E-Mail', icon: '✉️' },
  { value: 'phone', label: 'Telefon', icon: '📱' },
  { value: 'date', label: 'Datum', icon: '📅' },
  { value: 'time', label: 'Uhrzeit', icon: '🕐' },
  { value: 'number', label: 'Zahl', icon: '🔢' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'radio', label: 'Radio-Buttons', icon: '⭕' },
  { value: 'checkbox', label: 'Checkboxen', icon: '☑️' },
  { value: 'file', label: 'Datei-Upload', icon: '📎' },
  { value: 'heading', label: 'Überschrift', icon: '📌' },
  { value: 'info', label: 'Info-Text', icon: 'ℹ️' },
  { value: 'repeatable_group', label: 'Wiederholbare Gruppe', icon: '🔁' },
  { value: 'composite', label: 'Feldverknüpfung', icon: '🔗' },
];

// Field types available for group fields (excludes repeatable_group and composite to prevent nesting)
const GROUP_FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'textarea', label: 'Textbereich', icon: '📄' },
  { value: 'email', label: 'E-Mail', icon: '✉️' },
  { value: 'phone', label: 'Telefon', icon: '📱' },
  { value: 'date', label: 'Datum', icon: '📅' },
  { value: 'time', label: 'Uhrzeit', icon: '🕐' },
  { value: 'number', label: 'Zahl', icon: '🔢' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'radio', label: 'Radio-Buttons', icon: '⭕' },
  { value: 'checkbox', label: 'Checkboxen', icon: '☑️' },
];

// Field types available for composite sub-fields
const COMPOSITE_FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'date', label: 'Datum', icon: '📅' },
];

const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const FormEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [globalDropdowns, setGlobalDropdowns] = useState([]);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [selectedGroupFieldIndex, setSelectedGroupFieldIndex] = useState(null);
  const [fieldTypeSelectKey, setFieldTypeSelectKey] = useState(0);
  const [groupFieldSelectKeys, setGroupFieldSelectKeys] = useState({});
  const [compositeFieldSelectKeys, setCompositeFieldSelectKeys] = useState({});
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  
  // Versioning state
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [activeTab, setActiveTab] = useState('editor');

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    access_type: 'public',
    password: '',
    department_id: user?.department_id || '',
    logo_url: null,
    bot_protection: 'off',
    sender_field_id: null,
    is_anonymous: false,
    fields: []
  });

  useEffect(() => {
    loadData();
    // Check if creating from template
    const templateId = searchParams.get('template');
    if (templateId && !id) {
      loadTemplate(templateId);
    }
  }, [id]);

  const loadTemplate = async (templateId) => {
    try {
      const res = await axios.get(`${API_URL}/api/form-templates/${templateId}`, { headers: getAuthHeader() });
      const template = res.data;
      setFormData(prev => ({
        ...prev,
        name: template.name ? `${template.name} (Kopie)` : '',
        description: template.description || '',
        fields: template.fields || [],
      }));
      toast.success('Vorlage geladen');
    } catch (error) {
      toast.error('Vorlage konnte nicht geladen werden');
    }
  };

  const loadVersions = async () => {
    if (!id) return;
    setVersionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/forms/${id}/versions`, { headers: getAuthHeader() });
      setVersions(res.data || []);
    } catch (error) {
      toast.error('Versionen konnten nicht geladen werden');
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!selectedVersion) return;
    try {
      await axios.post(`${API_URL}/api/forms/${id}/rollback/${selectedVersion.version}`, {}, { headers: getAuthHeader() });
      toast.success(t('forms.rollbackSuccess'));
      setRollbackDialogOpen(false);
      // Reload form data
      const updatedForm = await getForm(id);
      setFormData(updatedForm);
      loadVersions();
    } catch (error) {
      toast.error('Rollback fehlgeschlagen');
    }
  };

  const loadData = async () => {
    try {
      const [depts, dropdowns] = await Promise.all([
        getDepartments(),
        getGlobalDropdowns().catch(() => [])
      ]);
      setDepartments(depts);
      setGlobalDropdowns(dropdowns);

      if (id) {
        const form = await getForm(id);
        setFormData({
          name: form.name,
          title: form.title || '',
          description: form.description || '',
          access_type: form.access_type || 'public',
          password: form.password || '',
          department_id: form.department_id || '',
          logo_url: form.logo_url || null,
          bot_protection: form.bot_protection || 'off',
          fields: (form.fields || []).map((f, i) => ({ ...f, order: f.order ?? i }))
        });
        if (form.logo_url) {
          setLogoPreview(`${API_URL}${form.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const addField = (type = 'text') => {
    const newField = {
      field_id: generateFieldId(),
      label: '',
      field_type: type,
      required: false,
      placeholder: '',
      help_text: '',
      options: [],
      global_dropdown_id: null,
      use_regions: false,
      order: formData.fields.length,
      min_length: null,
      max_length: null,
      min_value: null,
      max_value: null,
      pattern: null,
      visible_for_roles: null,
      conditional_visibility: null,
      allowed_file_types: type === 'file' ? ['.pdf', '.jpg', '.jpeg', '.png'] : null,
      max_file_size_mb: type === 'file' ? 10 : null,
      group_fields: type === 'repeatable_group' ? [] : null,
      min_repeats: type === 'repeatable_group' ? 1 : null,
      max_repeats: type === 'repeatable_group' ? 10 : null,
      // Composite field settings
      composite_fields: type === 'composite' ? [] : null,
      composite_separator: type === 'composite' ? ' ' : null,
      composite_format: null,
    };
    
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    setSelectedFieldIndex(formData.fields.length);
  };

  const updateField = (index, updates) => {
    setFormData(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], ...updates };
      return { ...prev, fields: newFields };
    });
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index).map((f, i) => ({ ...f, order: i }))
    }));
    setSelectedFieldIndex(null);
  };

  const duplicateField = (index) => {
    const field = formData.fields[index];
    const newField = {
      ...field,
      field_id: generateFieldId(),
      label: `${field.label} (Kopie)`,
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const moveField = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formData.fields.length) return;
    
    setFormData(prev => {
      const newFields = [...prev.fields];
      const [removed] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, removed);
      return { ...prev, fields: newFields.map((f, i) => ({ ...f, order: i })) };
    });
    setSelectedFieldIndex(toIndex);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    moveField(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target?.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_url: null }));
  };

  const addGroupField = (parentIndex, fieldType = 'text') => {
    const newGroupField = {
      field_id: generateFieldId(),
      label: '',
      field_type: fieldType,
      required: false,
      placeholder: '',
      options: ['select', 'radio', 'checkbox'].includes(fieldType) ? ['Option 1'] : null,
    };
    
    updateField(parentIndex, {
      group_fields: [...(formData.fields[parentIndex].group_fields || []), newGroupField]
    });
    
    // Reset the dropdown for this parent field
    setGroupFieldSelectKeys(prev => ({
      ...prev,
      [parentIndex]: (prev[parentIndex] || 0) + 1
    }));
  };

  const updateGroupField = (parentIndex, childIndex, updates) => {
    const parentField = formData.fields[parentIndex];
    const newGroupFields = [...(parentField.group_fields || [])];
    newGroupFields[childIndex] = { ...newGroupFields[childIndex], ...updates };
    updateField(parentIndex, { group_fields: newGroupFields });
  };

  const removeGroupField = (parentIndex, childIndex) => {
    const parentField = formData.fields[parentIndex];
    updateField(parentIndex, {
      group_fields: (parentField.group_fields || []).filter((_, i) => i !== childIndex)
    });
  };

  // Composite field management
  const addCompositeField = (parentIndex, fieldType = 'text') => {
    const newCompositeField = {
      field_id: generateFieldId(),
      label: '',
      field_type: fieldType,
      required: false,
      placeholder: '',
      options: fieldType === 'select' ? ['Option 1'] : null,
    };
    
    updateField(parentIndex, {
      composite_fields: [...(formData.fields[parentIndex].composite_fields || []), newCompositeField]
    });
    
    // Reset the dropdown for this parent field
    setCompositeFieldSelectKeys(prev => ({
      ...prev,
      [parentIndex]: (prev[parentIndex] || 0) + 1
    }));
  };

  const updateCompositeField = (parentIndex, childIndex, updates) => {
    const parentField = formData.fields[parentIndex];
    const newCompositeFields = [...(parentField.composite_fields || [])];
    newCompositeFields[childIndex] = { ...newCompositeFields[childIndex], ...updates };
    updateField(parentIndex, { composite_fields: newCompositeFields });
  };

  const removeCompositeField = (parentIndex, childIndex) => {
    const parentField = formData.fields[parentIndex];
    updateField(parentIndex, {
      composite_fields: (parentField.composite_fields || []).filter((_, i) => i !== childIndex)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    if (isSuperAdmin() && !formData.department_id) {
      toast.error('Bitte wählen Sie eine Abteilung');
      return;
    }

    // Validate sender field configuration
    if (!formData.is_anonymous && !formData.sender_field_id) {
      toast.error('Bitte wählen Sie ein Absender-Feld aus oder aktivieren Sie den Anonym-Modus');
      return;
    }

    setSaving(true);
    try {
      // Prepare fields with correct order
      const fieldsToSave = formData.fields.map((f, i) => ({ ...f, order: i }));

      const dataToSave = {
        ...formData,
        fields: fieldsToSave
      };

      let savedForm;
      if (id) {
        savedForm = await updateForm(id, dataToSave);
        toast.success('Formular aktualisiert');
      } else {
        savedForm = await createForm(dataToSave);
        toast.success('Formular erstellt');
      }

      // Upload logo if changed
      if (logoFile && savedForm?.id) {
        try {
          const logoFormData = new FormData();
          logoFormData.append('file', logoFile);
          
          const response = await fetch(`${API_URL}/api/forms/${savedForm.id}/logo`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: logoFormData
          });
          
          if (!response.ok) throw new Error('Logo upload failed');
          toast.success('Logo hochgeladen');
        } catch (logoError) {
          console.error('Logo upload error:', logoError);
          toast.error('Logo-Upload fehlgeschlagen');
        }
      }

      navigate('/admin/forms');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const selectedField = selectedFieldIndex !== null ? formData.fields[selectedFieldIndex] : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if user can directly publish
  const canDirectPublish = () => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (['department_head', 'deputy'].includes(user.role)) {
      return formData.department_id === user.department_id;
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? t('forms.editForm') : t('forms.newForm')}
          </h1>
          <p className="text-gray-500 mt-1">{t('formEditor.subtitle')}</p>
          {canDirectPublish() && (
            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
              <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full"></span>
              {t('formEditor.roleOverrideActive')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/forms')}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Main Tabs for Editor/Versions */}
      {id && (
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); if (val === 'versions') loadVersions(); }}>
          <TabsList>
            <TabsTrigger value="editor">{t('formEditor.editor')}</TabsTrigger>
            <TabsTrigger value="versions">
              <History className="h-4 w-4 mr-2" />
              {t('forms.versions')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="versions" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('forms.versionHistory')}</CardTitle>
                <CardDescription>Alle Versionen dieses Formulars</CardDescription>
              </CardHeader>
              <CardContent>
                {versionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : versions.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Keine Versionen vorhanden</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Version</TableHead>
                        <TableHead>Datum</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Ersteller</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {versions.map((version, idx) => (
                        <TableRow key={version.version || idx}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">v{version.version}</span>
                              {idx === 0 && <Badge className="bg-emerald-100 text-emerald-700">{t('forms.currentVersion')}</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            {version.created_at ? format(new Date(version.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {version.status || (idx === 0 ? 'Live' : 'Archiviert')}
                            </Badge>
                          </TableCell>
                          <TableCell>{version.created_by_name || version.created_by || '-'}</TableCell>
                          <TableCell className="text-right">
                            {idx > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setSelectedVersion(version); setRollbackDialogOpen(true); }}
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                {t('forms.rollback')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="editor" className="mt-4">
            {/* Editor content wrapped in TabsContent */}
          </TabsContent>
        </Tabs>
      )}

      {/* Rollback Confirmation Dialog */}
      <AlertDialog open={rollbackDialogOpen} onOpenChange={setRollbackDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('forms.rollback')} zu Version {selectedVersion?.version}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('forms.rollbackWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRollback}>{t('common.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main Editor Grid - Show always for new forms, or when editor tab is active */}
      {(!id || activeTab === 'editor') && (
      <div className="grid grid-cols-3 gap-6">
        {/* Left Panel - Fields List */}
        <div className="col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('formEditor.basicSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('formEditor.placeholders.internalName', 'Interner Name')}
                  />
                </div>
                <div>
                  <Label>{t('formEditor.displayTitle')}</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('formEditor.placeholders.publicTitle', 'Öffentlicher Titel')}
                  />
                </div>
              </div>
              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('formEditor.placeholders.shortDescription', 'Kurze Beschreibung des Formulars')}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('formEditor.accessType')}</Label>
                  <Select value={formData.access_type} onValueChange={(val) => setFormData({ ...formData, access_type: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">{t('forms.public', 'Öffentlich')}</SelectItem>
                      <SelectItem value="password">Passwortgeschützt</SelectItem>
                      <SelectItem value="internal">Nur intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('settings.botProtection')}</Label>
                  <Select value={formData.bot_protection || 'off'} onValueChange={(val) => setFormData({ ...formData, bot_protection: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">{t('settings.botProtectionOff')}</SelectItem>
                      <SelectItem value="auto">{t('settings.botProtectionAuto')}</SelectItem>
                      <SelectItem value="always">{t('settings.botProtectionAlways')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {formData.access_type === 'password' && (
                  <div>
                    <Label>Passwort</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Zugangscode"
                    />
                  </div>
                )}
                {isSuperAdmin() && (
                  <div>
                    <Label>{t('formEditor.department')} *</Label>
                    <Select value={formData.department_id} onValueChange={(val) => setFormData({ ...formData, department_id: val })}>
                      <SelectTrigger><SelectValue placeholder="Wählen..." /></SelectTrigger>
                      <SelectContent>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Logo Upload */}
              <div>
                <Label>{t('formEditor.formLogo')}</Label>
                <div className="mt-2 flex items-start gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img src={logoPreview} alt="Logo" className="h-20 w-auto object-contain border rounded" />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                        onClick={removeLogo}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="h-20 w-32 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <div className="text-center">
                        <Image className="h-6 w-6 mx-auto text-gray-400" />
                        <span className="text-xs text-gray-500">{t('common.upload')}</span>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Absender-Konfiguration */}
          <Card className="border-orange-200 bg-orange-50/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="text-orange-600">👤</span>
                Absender-Konfiguration
              </CardTitle>
              <CardDescription className="text-sm">
                Legen Sie fest, wie der Absender in der Einreichungsliste identifiziert wird
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Anonymous Mode Toggle */}
              <div className="flex items-start justify-between p-4 border rounded-lg bg-white">
                <div className="flex-1">
                  <Label className="text-base font-medium">Anonymes Formular</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Bei anonymen Umfragen wird kein Absender erfasst. In der Liste wird "Anonym" angezeigt.
                  </p>
                </div>
                <Switch
                  checked={formData.is_anonymous || false}
                  onCheckedChange={(checked) => {
                    setFormData({ 
                      ...formData, 
                      is_anonymous: checked,
                      sender_field_id: checked ? null : formData.sender_field_id
                    });
                  }}
                />
              </div>

              {/* Sender Field Selection */}
              {!formData.is_anonymous && (
                <div className="space-y-2">
                  <Label className="text-base font-medium">
                    Absender-Feld auswählen *
                  </Label>
                  <p className="text-sm text-gray-500">
                    Wählen Sie das Feld, das den Absender repräsentiert (Name, E-Mail oder Feldverknüpfung)
                  </p>
                  
                  {(() => {
                    const senderFields = formData.fields.filter(f => 
                      ['text', 'email', 'composite'].includes(f.field_type)
                    );
                    
                    if (senderFields.length === 0) {
                      return (
                        <div className="p-4 border border-amber-200 rounded-lg bg-amber-50 text-amber-800 text-sm">
                          <p className="font-medium">⚠️ Keine geeigneten Felder vorhanden</p>
                          <p className="mt-1">
                            Bitte fügen Sie zuerst ein Text-, E-Mail- oder Feldverknüpfungs-Feld hinzu.
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <Select 
                        value={formData.sender_field_id || ''} 
                        onValueChange={(val) => setFormData({ ...formData, sender_field_id: val })}
                      >
                        <SelectTrigger className={`${!formData.sender_field_id ? 'border-red-300' : ''}`}>
                          <SelectValue placeholder="Absender-Feld auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {senderFields.map((field) => (
                            <SelectItem key={field.field_id} value={field.field_id}>
                              <div className="flex items-center gap-2">
                                <span>{FIELD_TYPES.find(t => t.value === field.field_type)?.icon}</span>
                                <span>{field.label || '(Ohne Bezeichnung)'}</span>
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {FIELD_TYPES.find(t => t.value === field.field_type)?.label}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                  
                  {!formData.sender_field_id && formData.fields.some(f => ['text', 'email', 'composite'].includes(f.field_type)) && (
                    <p className="text-sm text-red-600">
                      ⚠️ Bitte wählen Sie ein Absender-Feld aus
                    </p>
                  )}
                </div>
              )}

              {/* Info Box */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                <p className="font-medium mb-1">ℹ️ Hinweis:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Das gewählte Feld wird in der Einreichungsliste als "Absender" angezeigt</li>
                  <li>Datei-Upload-Felder können nicht als Absender verwendet werden</li>
                  <li>Bei Feldverknüpfungen werden alle Sub-Felder kombiniert</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Fields */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('formEditor.formFields')} ({formData.fields.length})</CardTitle>
                <Select 
                  key={fieldTypeSelectKey}
                  onValueChange={(type) => {
                    addField(type);
                    setFieldTypeSelectKey(prev => prev + 1); // Reset dropdown
                  }}
                >
                  <SelectTrigger className="w-48">
                    <Plus className="w-4 h-4 mr-2" />
                    <SelectValue placeholder={t('formEditor.addFieldDropdown')} />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="mr-2">{type.icon}</span>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {formData.fields.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Plus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Noch keine Felder. Wählen Sie oben einen Feldtyp aus.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.fields.map((field, index) => (
                    <div
                      key={field.field_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      onClick={() => { setSelectedFieldIndex(index); setSelectedGroupFieldIndex(null); }}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedFieldIndex === index && selectedGroupFieldIndex === null
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      } ${draggedIndex === index ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                        <span className="text-lg">{FIELD_TYPES.find(t => t.value === field.field_type)?.icon || '📝'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{field.label || '(Ohne Bezeichnung)'}</span>
                            {field.required && <span className="text-red-500 text-sm">*</span>}
                          </div>
                          <span className="text-xs text-gray-500">{FIELD_TYPES.find(t => t.value === field.field_type)?.label}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); moveField(index, index - 1); }}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); moveField(index, index + 1); }}
                            disabled={index === formData.fields.length - 1}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); duplicateField(index); }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); removeField(index); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Repeatable Group Children */}
                      {field.field_type === 'repeatable_group' && (
                        <div className="mt-3 ml-8 space-y-2 border-l-2 border-blue-200 pl-4">
                          <p className="text-xs text-gray-500 mb-2">Gruppen-Felder:</p>
                          {(field.group_fields || []).map((gf, gi) => (
                            <div 
                              key={gf.field_id} 
                              className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-all ${
                                selectedFieldIndex === index && selectedGroupFieldIndex === gi 
                                  ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
                                  : 'bg-white hover:bg-gray-50'
                              }`}
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedFieldIndex(index);
                                setSelectedGroupFieldIndex(gi);
                              }}
                            >
                              <span className="text-sm font-medium">{gf.label || '(Ohne Bezeichnung)'}</span>
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                                {GROUP_FIELD_TYPES.find(t => t.value === gf.field_type)?.icon} {GROUP_FIELD_TYPES.find(t => t.value === gf.field_type)?.label}
                              </span>
                              {gf.required && <span className="text-xs text-red-500">*</span>}
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="ml-auto h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  removeGroupField(index, gi);
                                  if (selectedGroupFieldIndex === gi) setSelectedGroupFieldIndex(null);
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          
                          {/* Add Group Field Dropdown */}
                          <Select
                            key={groupFieldSelectKeys[index] || 0}
                            onValueChange={(type) => {
                              addGroupField(index, type);
                            }}
                          >
                            <SelectTrigger className="w-full h-9 text-sm">
                              <div className="flex items-center gap-1">
                                <Plus className="h-3 w-3" />
                                <span>Gruppenfeld hinzufügen...</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {GROUP_FIELD_TYPES.map((ft) => (
                                <SelectItem key={ft.value} value={ft.value}>
                                  <span className="mr-2">{ft.icon}</span> {ft.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Composite Field Children */}
                      {field.field_type === 'composite' && (
                        <div className="mt-3 ml-8 space-y-2 border-l-2 border-emerald-200 pl-4">
                          <p className="text-xs text-gray-500 mb-2">Unterfelder (verknüpft):</p>
                          {(field.composite_fields || []).map((cf, ci) => (
                            <div 
                              key={cf.field_id} 
                              className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-all ${
                                selectedFieldIndex === index && selectedGroupFieldIndex === ci 
                                  ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200' 
                                  : 'bg-white hover:bg-gray-50'
                              }`}
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedFieldIndex(index);
                                setSelectedGroupFieldIndex(ci);
                              }}
                            >
                              <span className="text-sm font-medium">{cf.label || '(Ohne Bezeichnung)'}</span>
                              <span className="text-xs px-1.5 py-0.5 bg-emerald-100 rounded text-emerald-700">
                                {COMPOSITE_FIELD_TYPES.find(t => t.value === cf.field_type)?.icon} {COMPOSITE_FIELD_TYPES.find(t => t.value === cf.field_type)?.label}
                              </span>
                              {cf.required && <span className="text-xs text-red-500">*</span>}
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="ml-auto h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  removeCompositeField(index, ci);
                                  if (selectedGroupFieldIndex === ci) setSelectedGroupFieldIndex(null);
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          
                          {/* Add Composite Sub-Field Dropdown */}
                          <Select
                            key={compositeFieldSelectKeys[index] || 0}
                            onValueChange={(type) => {
                              addCompositeField(index, type);
                            }}
                          >
                            <SelectTrigger className="w-full h-9 text-sm bg-emerald-50 border-emerald-200">
                              <div className="flex items-center gap-1 text-emerald-700">
                                <Plus className="h-3 w-3" />
                                <span>Unterfeld hinzufügen...</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {COMPOSITE_FIELD_TYPES.map((ft) => (
                                <SelectItem key={ft.value} value={ft.value}>
                                  <span className="mr-2">{ft.icon}</span> {ft.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Field Settings */}
        <div className="col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>{t('formEditor.fieldSettings')}</CardTitle>
              <CardDescription>
                {selectedGroupFieldIndex !== null && selectedField?.field_type === 'repeatable_group'
                  ? `Gruppenfeld: ${selectedField.group_fields?.[selectedGroupFieldIndex]?.label || 'Neues Gruppenfeld'}`
                  : selectedGroupFieldIndex !== null && selectedField?.field_type === 'composite'
                    ? `Unterfeld: ${selectedField.composite_fields?.[selectedGroupFieldIndex]?.label || 'Neues Unterfeld'}`
                    : selectedField 
                      ? `Bearbeite: ${selectedField.label || 'Neues Feld'}` 
                      : t('formEditor.selectField')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Composite Sub-Field Settings */}
              {selectedGroupFieldIndex !== null && selectedField?.field_type === 'composite' && selectedField.composite_fields?.[selectedGroupFieldIndex] ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-emerald-600">Unterfeld bearbeiten</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedGroupFieldIndex(null)}
                    >
                      Zurück zur Verknüpfung
                    </Button>
                  </div>
                  
                  {(() => {
                    const cf = selectedField.composite_fields[selectedGroupFieldIndex];
                    const updateCF = (updates) => updateCompositeField(selectedFieldIndex, selectedGroupFieldIndex, updates);
                    
                    return (
                      <div className="space-y-4">
                        <div>
                          <Label>Feldbezeichnung *</Label>
                          <Input
                            value={cf.label || ''}
                            onChange={(e) => updateCF({ label: e.target.value })}
                            placeholder="z.B. Vorname"
                          />
                        </div>
                        
                        <div>
                          <Label>Feldtyp</Label>
                          <Select
                            value={cf.field_type}
                            onValueChange={(val) => updateCF({ 
                              field_type: val,
                              options: val === 'select' ? (cf.options || ['Option 1']) : null
                            })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {COMPOSITE_FIELD_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  <span className="mr-2">{type.icon}</span>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Platzhalter</Label>
                          <Input
                            value={cf.placeholder || ''}
                            onChange={(e) => updateCF({ placeholder: e.target.value })}
                            placeholder="Platzhaltertext..."
                          />
                        </div>
                        
                        <div className="flex items-center justify-between py-2 border-t">
                          <Label className="text-sm">Pflichtfeld</Label>
                          <Switch
                            checked={cf.required || false}
                            onCheckedChange={(checked) => updateCF({ required: checked })}
                          />
                        </div>
                        
                        {/* Options for select dropdown */}
                        {cf.field_type === 'select' && (
                          <div className="space-y-2">
                            <Label>Optionen</Label>
                            {(cf.options || []).map((opt, optIdx) => (
                              <div key={optIdx} className="flex gap-2">
                                <Input
                                  value={opt}
                                  onChange={(e) => {
                                    const newOpts = [...(cf.options || [])];
                                    newOpts[optIdx] = e.target.value;
                                    updateCF({ options: newOpts });
                                  }}
                                  placeholder={`Option ${optIdx + 1}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newOpts = (cf.options || []).filter((_, i) => i !== optIdx);
                                    updateCF({ options: newOpts.length > 0 ? newOpts : ['Option 1'] });
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => updateCF({ options: [...(cf.options || []), `Option ${(cf.options || []).length + 1}`] })}
                            >
                              <Plus className="h-4 w-4 mr-1" /> Option hinzufügen
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : selectedGroupFieldIndex !== null && selectedField?.field_type === 'repeatable_group' && selectedField.group_fields?.[selectedGroupFieldIndex] ? (
                // Group Field Settings
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">Gruppenfeld bearbeiten</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedGroupFieldIndex(null)}
                    >
                      Zurück zur Gruppe
                    </Button>
                  </div>
                  
                  {(() => {
                    const gf = selectedField.group_fields[selectedGroupFieldIndex];
                    const updateGF = (updates) => updateGroupField(selectedFieldIndex, selectedGroupFieldIndex, updates);
                    
                    return (
                      <div className="space-y-4">
                        <div>
                          <Label>Feldbezeichnung *</Label>
                          <Input
                            value={gf.label || ''}
                            onChange={(e) => updateGF({ label: e.target.value })}
                            placeholder="z.B. Vorname"
                          />
                        </div>
                        
                        <div>
                          <Label>Feldtyp</Label>
                          <Select
                            value={gf.field_type}
                            onValueChange={(val) => updateGF({ 
                              field_type: val,
                              options: ['select', 'radio', 'checkbox'].includes(val) ? (gf.options || ['Option 1']) : null
                            })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {GROUP_FIELD_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  <span className="mr-2">{type.icon}</span>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {!['heading', 'info'].includes(gf.field_type) && (
                          <div>
                            <Label>Platzhalter</Label>
                            <Input
                              value={gf.placeholder || ''}
                              onChange={(e) => updateGF({ placeholder: e.target.value })}
                              placeholder="Platzhaltertext..."
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between py-2 border-t">
                          <Label className="text-sm">Pflichtfeld</Label>
                          <Switch
                            checked={gf.required || false}
                            onCheckedChange={(checked) => updateGF({ required: checked })}
                          />
                        </div>
                        
                        {/* Options for select/radio/checkbox */}
                        {['select', 'radio', 'checkbox'].includes(gf.field_type) && (
                          <div className="space-y-2">
                            <Label>Optionen</Label>
                            {(gf.options || []).map((opt, optIdx) => (
                              <div key={optIdx} className="flex gap-2">
                                <Input
                                  value={opt}
                                  onChange={(e) => {
                                    const newOpts = [...(gf.options || [])];
                                    newOpts[optIdx] = e.target.value;
                                    updateGF({ options: newOpts });
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-10 px-2"
                                  onClick={() => {
                                    const newOpts = (gf.options || []).filter((_, i) => i !== optIdx);
                                    updateGF({ options: newOpts });
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => updateGF({ options: [...(gf.options || []), `Option ${(gf.options || []).length + 1}`] })}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Option hinzufügen
                            </Button>
                          </div>
                        )}
                        
                        {/* Validation for text fields */}
                        {['text', 'textarea', 'email', 'phone'].includes(gf.field_type) && (
                          <div className="space-y-2 pt-2 border-t">
                            <Label className="text-xs text-gray-500">Validierung</Label>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Min. Länge</Label>
                                <Input
                                  type="number"
                                  value={gf.min_length || ''}
                                  onChange={(e) => updateGF({ min_length: parseInt(e.target.value) || null })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Max. Länge</Label>
                                <Input
                                  type="number"
                                  value={gf.max_length || ''}
                                  onChange={(e) => updateGF({ max_length: parseInt(e.target.value) || null })}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Number validation */}
                        {gf.field_type === 'number' && (
                          <div className="space-y-2 pt-2 border-t">
                            <Label className="text-xs text-gray-500">Validierung</Label>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Min. Wert</Label>
                                <Input
                                  type="number"
                                  value={gf.min_value || ''}
                                  onChange={(e) => updateGF({ min_value: parseFloat(e.target.value) || null })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Max. Wert</Label>
                                <Input
                                  type="number"
                                  value={gf.max_value || ''}
                                  onChange={(e) => updateGF({ max_value: parseFloat(e.target.value) || null })}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : selectedField ? (
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basis</TabsTrigger>
                    <TabsTrigger value="validation">Validierung</TabsTrigger>
                    <TabsTrigger value="advanced">Erweitert</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4 mt-4">
                    <div>
                      <Label>Feldbezeichnung *</Label>
                      <Input
                        value={selectedField.label}
                        onChange={(e) => updateField(selectedFieldIndex, { label: e.target.value })}
                        placeholder="z.B. Vorname"
                      />
                    </div>

                    <div>
                      <Label>Feldtyp</Label>
                      <Select
                        value={selectedField.field_type}
                        onValueChange={(val) => updateField(selectedFieldIndex, { field_type: val })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FIELD_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <span className="mr-2">{type.icon}</span>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Platzhalter</Label>
                      <Input
                        value={selectedField.placeholder || ''}
                        onChange={(e) => updateField(selectedFieldIndex, { placeholder: e.target.value })}
                        placeholder="Hinweistext im Feld"
                      />
                    </div>

                    <div>
                      <Label>Hilfetext</Label>
                      <Textarea
                        value={selectedField.help_text || ''}
                        onChange={(e) => updateField(selectedFieldIndex, { help_text: e.target.value })}
                        placeholder="Zusätzliche Erklärung für Benutzer"
                        rows={2}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Pflichtfeld</Label>
                      <Switch
                        checked={selectedField.required}
                        onCheckedChange={(val) => updateField(selectedFieldIndex, { required: val })}
                      />
                    </div>

                    {/* Options for select/radio/checkbox */}
                    {['select', 'radio', 'checkbox'].includes(selectedField.field_type) && (
                      <>
                        <div>
                          <Label>Optionen-Quelle</Label>
                          <Select
                            value={selectedField.global_dropdown_id || (selectedField.use_regions ? 'regions' : 'manual')}
                            onValueChange={(val) => {
                              if (val === 'manual') {
                                updateField(selectedFieldIndex, { global_dropdown_id: null, use_regions: false });
                              } else if (val === 'regions') {
                                updateField(selectedFieldIndex, { global_dropdown_id: null, use_regions: true });
                              } else {
                                updateField(selectedFieldIndex, { global_dropdown_id: val, use_regions: false });
                              }
                            }}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">Manuelle Eingabe</SelectItem>
                              {globalDropdowns.map((dd) => (
                                <SelectItem key={dd.id} value={dd.id}>{dd.display_name || dd.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {!selectedField.global_dropdown_id && !selectedField.use_regions && (
                          <div>
                            <Label>Optionen (eine pro Zeile)</Label>
                            <Textarea
                              value={selectedField.options?.join('\n') || ''}
                              onChange={(e) => updateField(selectedFieldIndex, { 
                                options: e.target.value.split('\n').filter(o => o.trim()) 
                              })}
                              rows={4}
                              placeholder="Option 1\nOption 2\nOption 3"
                            />
                          </div>
                        )}
                      </>
                    )}

                    {/* File upload settings */}
                    {selectedField.field_type === 'file' && (
                      <>
                        <div>
                          <Label>Erlaubte Dateitypen</Label>
                          <Input
                            value={selectedField.allowed_file_types?.join(', ') || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { 
                              allowed_file_types: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                            })}
                            placeholder=".pdf, .jpg, .png"
                          />
                        </div>
                        <div>
                          <Label>Max. Dateigröße (MB)</Label>
                          <Input
                            type="number"
                            value={selectedField.max_file_size_mb || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { max_file_size_mb: parseInt(e.target.value) || null })}
                            placeholder="10"
                          />
                        </div>
                      </>
                    )}

                    {/* Repeatable group settings */}
                    {selectedField.field_type === 'repeatable_group' && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Min. Wiederholungen</Label>
                            <Input
                              type="number"
                              value={selectedField.min_repeats || 1}
                              onChange={(e) => updateField(selectedFieldIndex, { min_repeats: parseInt(e.target.value) || 1 })}
                            />
                          </div>
                          <div>
                            <Label>Max. Wiederholungen</Label>
                            <Input
                              type="number"
                              value={selectedField.max_repeats || 10}
                              onChange={(e) => updateField(selectedFieldIndex, { max_repeats: parseInt(e.target.value) || 10 })}
                            />
                          </div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded text-sm">
                          <p className="font-medium text-blue-700">Gruppenfelder</p>
                          <p className="text-blue-600 mt-1">Fügen Sie Felder zur Gruppe über den &quot;+ Gruppenfeld&quot; Button in der Feldliste hinzu.</p>
                        </div>
                      </>
                    )}

                    {/* Composite field settings */}
                    {selectedField.field_type === 'composite' && (
                      <>
                        <div>
                          <Label>Trennzeichen</Label>
                          <Input
                            value={selectedField.composite_separator || ' '}
                            onChange={(e) => updateField(selectedFieldIndex, { composite_separator: e.target.value })}
                            placeholder="z.B. Leerzeichen, Komma, Bindestrich"
                          />
                          <p className="text-xs text-gray-500 mt-1">Wird zwischen den Unterfeldern eingefügt</p>
                        </div>
                        <div>
                          <Label>Format (optional)</Label>
                          <Input
                            value={selectedField.composite_format || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { composite_format: e.target.value || null })}
                            placeholder="z.B. {titel} {vorname} {nachname}"
                          />
                          <p className="text-xs text-gray-500 mt-1">Benutzerdefiniertes Format mit Feld-IDs in Klammern</p>
                        </div>
                        <div className="bg-emerald-50 p-3 rounded text-sm border border-emerald-200">
                          <p className="font-medium text-emerald-700">🔗 Feldverknüpfung</p>
                          <p className="text-emerald-600 mt-1">
                            Kombiniert mehrere Unterfelder zu einem logischen Feld (z.B. Vorname + Nachname = Name).
                            Fügen Sie Unterfelder über &quot;+ Unterfeld&quot; in der Feldliste hinzu.
                          </p>
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="validation" className="space-y-4 mt-4">
                    {['text', 'textarea', 'email', 'phone'].includes(selectedField.field_type) && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Min. Länge</Label>
                            <Input
                              type="number"
                              value={selectedField.min_length || ''}
                              onChange={(e) => updateField(selectedFieldIndex, { min_length: parseInt(e.target.value) || null })}
                            />
                          </div>
                          <div>
                            <Label>Max. Länge</Label>
                            <Input
                              type="number"
                              value={selectedField.max_length || ''}
                              onChange={(e) => updateField(selectedFieldIndex, { max_length: parseInt(e.target.value) || null })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Regex-Pattern</Label>
                          <Input
                            value={selectedField.pattern || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { pattern: e.target.value || null })}
                            placeholder="z.B. ^[A-Z].*"
                          />
                        </div>
                      </>
                    )}

                    {selectedField.field_type === 'number' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Min. Wert</Label>
                          <Input
                            type="number"
                            value={selectedField.min_value || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { min_value: parseFloat(e.target.value) || null })}
                          />
                        </div>
                        <div>
                          <Label>Max. Wert</Label>
                          <Input
                            type="number"
                            value={selectedField.max_value || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { max_value: parseFloat(e.target.value) || null })}
                          />
                        </div>
                      </div>
                    )}

                    {!['text', 'textarea', 'email', 'phone', 'number'].includes(selectedField.field_type) && (
                      <p className="text-gray-500 text-sm">Keine Validierungsoptionen für diesen Feldtyp verfügbar.</p>
                    )}
                  </TabsContent>

                  <TabsContent value="advanced" className="space-y-4 mt-4">
                    <div>
                      <Label>Bedingte Sichtbarkeit</Label>
                      <p className="text-xs text-gray-500 mb-2">Dieses Feld wird nur angezeigt, wenn...</p>
                      <Select
                        value={selectedField.conditional_visibility?.field_id || '__none__'}
                        onValueChange={(val) => {
                          if (val === '__none__') {
                            updateField(selectedFieldIndex, { conditional_visibility: null });
                          } else {
                            updateField(selectedFieldIndex, { 
                              conditional_visibility: { 
                                field_id: val, 
                                value: selectedField.conditional_visibility?.value || '' 
                              }
                            });
                          }
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Keine Bedingung" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Keine Bedingung</SelectItem>
                          {formData.fields
                            .filter(f => f.field_id !== selectedField.field_id && ['select', 'radio', 'checkbox'].includes(f.field_type))
                            .map((f) => (
                              <SelectItem key={f.field_id} value={f.field_id}>{f.label || f.field_id}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>

                      {selectedField.conditional_visibility?.field_id && (
                        <div className="mt-2">
                          <Label>...diesen Wert hat:</Label>
                          <Input
                            value={selectedField.conditional_visibility?.value || ''}
                            onChange={(e) => updateField(selectedFieldIndex, { 
                              conditional_visibility: { 
                                ...selectedField.conditional_visibility, 
                                value: e.target.value 
                              }
                            })}
                            placeholder="Erwarteter Wert"
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Sichtbar für Rollen</Label>
                      <p className="text-xs text-gray-500 mb-2">Leer = alle Rollen</p>
                      <div className="space-y-2">
                        {['super_admin', 'department_head', 'deputy', 'user'].map((role) => (
                          <div key={role} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={(selectedField.visible_for_roles || []).includes(role)}
                              onChange={(e) => {
                                const current = selectedField.visible_for_roles || [];
                                const newRoles = e.target.checked
                                  ? [...current, role]
                                  : current.filter(r => r !== role);
                                updateField(selectedFieldIndex, { 
                                  visible_for_roles: newRoles.length > 0 ? newRoles : null 
                                });
                              }}
                              className="rounded"
                            />
                            <Label className="font-normal capitalize">{role.replace('_', ' ')}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-xs text-gray-400 border-t pt-4">
                      <p>Feld-ID: {selectedField.field_id}</p>
                      <p>Reihenfolge: {selectedFieldIndex + 1}</p>
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>{t('formEditor.clickToSelect')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
};

export default FormEditor;
